import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { z } from 'zod';

const resetSchema = z.object({
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }).max(72),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event which fires when user clicks the email link
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasRecoverySession(true);
      }
      setSessionChecked(true);
    });

    // Also check if there's already a session (user may have already landed)
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Check URL hash for recovery type
      const hash = window.location.hash;
      if (hash && hash.includes('type=recovery')) {
        setHasRecoverySession(true);
      } else if (session) {
        // User has an active session - they may have navigated here directly
        setHasRecoverySession(true);
      }
      setSessionChecked(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setValidationErrors({});

    const result = resetSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        errors[field] = err.message;
      });
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsSubmitting(false);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Reset Failed',
        description: error.message.includes('same_password')
          ? 'New password must be different from your current password.'
          : error.message,
      });
      return;
    }

    setIsSuccess(true);
    toast({ title: 'Password Reset', description: 'Your password has been updated successfully.' });

    // Sign out and redirect to login after 3 seconds
    setTimeout(async () => {
      await supabase.auth.signOut();
      navigate('/auth', { replace: true });
    }, 3000);
  }

  if (!sessionChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="header-gradient py-6">
        <div className="container px-4">
          <span className="logo-text text-[28px] text-white tracking-tight">
            Veracord<span style={{ fontSize: '9px', fontWeight: 400, position: 'relative', top: '-8px', marginLeft: '1px', letterSpacing: 0, fontFamily: 'Inter, sans-serif' }}>™</span>
          </span>
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md cv-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">
              {isSuccess ? 'Password Updated' : 'Reset Your Password'}
            </CardTitle>
            <CardDescription>
              {isSuccess
                ? 'You will be redirected to sign in shortly.'
                : 'Enter your new password below.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSuccess ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <CheckCircle2 className="h-12 w-12 text-primary" />
                <p className="text-sm text-muted-foreground">Redirecting to sign in…</p>
              </div>
            ) : !hasRecoverySession ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <AlertCircle className="h-12 w-12 text-destructive" />
                <p className="text-sm text-muted-foreground text-center">
                  This reset link has expired or is invalid. Please request a new password reset.
                </p>
                <Button variant="outline" onClick={() => navigate('/auth', { replace: true })}>
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="text-sm text-destructive">{validationErrors.password}</p>
                  )}
                  {/* Strength indicator */}
                  {password.length > 0 && (
                    <div className="flex gap-1 pt-1">
                      {[1, 2, 3, 4].map((level) => {
                        const strength = password.length >= 12 ? 4 : password.length >= 8 ? 3 : password.length >= 6 ? 2 : 1;
                        return (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full transition-colors ${
                              level <= strength
                                ? strength <= 1
                                  ? 'bg-destructive'
                                  : strength === 2
                                    ? 'bg-orange-400'
                                    : strength === 3
                                      ? 'bg-yellow-400'
                                      : 'bg-green-500'
                                : 'bg-muted'
                            }`}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p className="text-sm text-destructive">{validationErrors.confirmPassword}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
